// ADR-0145 — la bandeja de consultas del propietario (fase 2, corte 1,
// #0738). Lado del staff en core-api: la lista por proyecto, el hilo, la
// respuesta y la marca de lectura. El alcance (qué proyectos ve un jefe)
// lo decide el servidor con `loadScopedAssets`; acá sólo se parsea el
// wire (ADR-0021/D6).

import {
  ownerInquiryAttachmentUploadUrlResponseSchema,
  ownerInquiryAttachmentUrlsResponseSchema,
  ownerInquiryDetailSchema,
  ownerInquiryReplyResponseSchema,
  ownerInquiryStaffListResponseSchema,
  type OwnerInquiryAttachmentUploadUrlRequest,
  type OwnerInquiryAttachmentUploadUrlResponse,
  type OwnerInquiryAttachmentUrlsResponse,
  type OwnerInquiryDetail,
  type OwnerInquiryReplyRequest,
  type OwnerInquiryReplyResponse,
  type OwnerInquiryStaffListResponse,
} from "@bitacorapro/api-types";
import { request } from "../client";

const base = (id: string) => `/owner-inquiries/${encodeURIComponent(id)}`;

/** GET /owner-inquiries?assetGroupId= — todas las consultas del proyecto
 *  con correo entregado, `createdAt DESC`. Sin paginación en el servidor:
 *  la pantalla pagina en el cliente como la lista de observaciones. */
export async function getOwnerInquiries(
  assetGroupId: string
): Promise<OwnerInquiryStaffListResponse> {
  const json = await request<unknown>(
    "GET",
    "core",
    `/owner-inquiries?assetGroupId=${encodeURIComponent(assetGroupId)}`
  );
  return ownerInquiryStaffListResponseSchema.parse(json);
}

/** GET /owner-inquiries/:id — la fila más el hilo completo y la marca
 *  previa de quien pregunta (`lastReadAt`, para pintar «N nuevos»). */
export async function getOwnerInquiry(id: string): Promise<OwnerInquiryDetail> {
  const json = await request<unknown>("GET", "core", base(id));
  return ownerInquiryDetailSchema.parse(json);
}

/** POST /owner-inquiries/:id/messages — la respuesta del staff. La clave
 *  de idempotencia es obligatoria: el drawer la ata al texto, así un
 *  reintento tras perder la respuesta devuelve el mismo mensaje sin
 *  reenviar el correo. */
export async function replyOwnerInquiry(
  id: string,
  body: OwnerInquiryReplyRequest,
  opts: { idempotencyKey: string }
): Promise<OwnerInquiryReplyResponse> {
  const json = await request<unknown>("POST", "core", `${base(id)}/messages`, {
    body,
    headers: { "Idempotency-Key": opts.idempotencyKey },
  });
  return ownerInquiryReplyResponseSchema.parse(json);
}

/** POST /owner-inquiries/:id/read — `lastReadAt` es el `createdAt` de la
 *  última entrada que se mostró, nunca el reloj del cliente. 204. */
export async function markOwnerInquiryRead(id: string, lastReadAt: string): Promise<void> {
  await request<unknown>("POST", "core", `${base(id)}/read`, { body: { lastReadAt } });
}

// ADR-0158 (#0760) — adjuntos del chat. El staff sólo sube PDF (lo impone
// el servidor); el PUT va con `putToStorage` de documentos.

/** POST /owner-inquiries/:id/attachments/upload-url — PUT firmado al
 *  bucket privado (vence en 120 s: se pide justo antes de subir). */
export async function getOwnerInquiryAttachmentUploadUrl(
  id: string,
  body: OwnerInquiryAttachmentUploadUrlRequest
): Promise<OwnerInquiryAttachmentUploadUrlResponse> {
  const json = await request<unknown>("POST", "core", `${base(id)}/attachments/upload-url`, {
    body,
  });
  return ownerInquiryAttachmentUploadUrlResponseSchema.parse(json);
}

/** GET /owner-inquiries/:id/attachments/urls?ids= — GET firmados de vida
 *  corta, en lote (≤ 24). Las miniaturas y «Abrir» un PDF. */
export async function getOwnerInquiryAttachmentUrls(
  id: string,
  ids: readonly string[]
): Promise<OwnerInquiryAttachmentUrlsResponse> {
  const json = await request<unknown>(
    "GET",
    "core",
    `${base(id)}/attachments/urls?ids=${ids.map(encodeURIComponent).join(",")}`
  );
  return ownerInquiryAttachmentUrlsResponseSchema.parse(json);
}
